/**
 * L'arbre d'expression mathématique — le contrat BORNÉ entre ce qui est écrit
 * dans un énoncé et ce qui est dessiné à l'écran.
 *
 * Pourquoi un arbre et pas du LaTeX (FR-8.6, PRD §8) : le PRD demande « des
 * maths typographiées partout, jamais de texte brut », pas un moteur de
 * composition généraliste. Trois raisons de refuser une chaîne LaTeX libre :
 *
 *  1. **Le même rendu doit valoir dans l'app ET dans le courriel au parent.**
 *     C'est déjà l'argument qui a fait choisir cette architecture pour les
 *     figures (`../figures/resoudre.ts`, §4 de la spec de représentation
 *     visuelle) : une liste d'affichage résolue, partagée, que deux rendus
 *     consomment sans jamais diverger.
 *  2. **Aucun modèle ne doit pouvoir envoyer du balisage arbitraire dans une
 *     interface enfant.** Une expression structurée et bornée se VALIDE : un
 *     nœud hors de cette union n'existe pas. Une chaîne LaTeX ne se valide
 *     pas — elle s'exécute.
 *  3. Un moteur LaTeX côté client, c'est une WebView ou 300 ko de police
 *     mathématique. `react-native-svg` est déjà en service, et il rend la
 *     MÊME chose sur natif et sur web.
 *
 * L'arbre n'est jamais écrit par le modèle : il est ANALYSÉ depuis la prose
 * de l'énoncé (`analyser.ts`). Le modèle continue d'écrire `2/3 + 1/4` ; c'est
 * le produit qui décide que cela se dessine en fractions empilées.
 */
import { enExposantUnicode, enIndiceUnicode } from './unicode.js';
/**
 * Le nœud sous ses marques : `\cible{s}{-}` EST un signe moins, pour tout ce
 * qui se demande « opérateur ou opérande ? » (la description, la lecture).
 */
export function sansCible(noeud) {
    let n = noeud;
    while (n.type === 'cible')
        n = n.contenu;
    return n;
}
/* ------------------------------------------------------------ fabriques */
export const atome = (texte) => ({ type: 'atome', texte });
export const operateur = (texte) => ({ type: 'operateur', texte });
/** Une rangée d'un seul enfant n'existe pas : elle EST son enfant. */
export function rangee(enfants) {
    if (enfants.length === 1) {
        const seul = enfants[0];
        if (seul !== undefined)
            return seul;
    }
    return { type: 'rangee', enfants };
}
/* ------------------------------------------------------------ inspection */
/** Les enfants d'un nœud, dans l'ordre de lecture. Un seul endroit à tenir. */
export function enfantsDe(noeud) {
    switch (noeud.type) {
        case 'atome':
        case 'operateur':
            return [];
        case 'rangee':
            return noeud.enfants;
        case 'fraction':
            return [noeud.numerateur, noeud.denominateur];
        case 'script':
            return [noeud.base, noeud.exposant, noeud.indice].filter((n) => n !== undefined);
        case 'racine':
            return noeud.degre === undefined ? [noeud.radicande] : [noeud.degre, noeud.radicande];
        case 'valeurAbsolue':
        case 'norme':
            return [noeud.contenu];
        case 'surmonte':
            return [noeud.contenu];
        case 'groupe':
            return [noeud.contenu];
        case 'systeme':
            return noeud.lignes;
        case 'grandOperateur':
            return [noeud.borneInf, noeud.borneSup, noeud.corps].filter((n) => n !== undefined);
        case 'cible':
            return [noeud.contenu];
        case 'sousAccolade':
            return [noeud.contenu, noeud.legende];
        case 'matrice':
            return noeud.lignes.flat();
    }
}
/** `true` si `predicat` vaut pour ce nœud ou pour l'un de ses descendants. */
export function contientMath(noeud, predicat) {
    if (predicat(noeud))
        return true;
    return enfantsDe(noeud).some((enfant) => contientMath(enfant, predicat));
}
/**
 * Le texte littéral d'un nœud « plat » — un atome, un opérateur, ou une
 * rangée de ceux-ci — ou `null` dès qu'il porte une structure. Sert à savoir
 * si un exposant tient en caractères Unicode.
 */
export function texteLitteralDe(noeud) {
    if (noeud.type === 'atome' || noeud.type === 'operateur')
        return noeud.texte;
    // Une cible est transparente : `3^{\cible{s}{2}}` reste un carré Unicode.
    if (noeud.type === 'cible')
        return texteLitteralDe(noeud.contenu);
    if (noeud.type !== 'rangee')
        return null;
    let resultat = '';
    for (const enfant of noeud.enfants) {
        const morceau = texteLitteralDe(enfant);
        if (morceau === null)
            return null;
        resultat += morceau;
    }
    return resultat;
}
/**
 * `true` si ce script (exposant / indice) ne tient PAS en Unicode — donc s'il
 * faut le dessiner. `x²` reste du texte ; `x^{n+1}` et `x_1^2` sont dessinés.
 */
function scriptExigeDessin(noeud) {
    const { exposant, indice } = noeud;
    // Les deux à la fois : Unicode les mettrait côte à côte (`x₁²`), ce qui ne
    // dit pas lequel porte quoi. On dessine.
    if (exposant !== undefined && indice !== undefined)
        return true;
    if (exposant !== undefined) {
        const litteral = texteLitteralDe(exposant);
        return litteral === null || enExposantUnicode(litteral) === null;
    }
    if (indice !== undefined) {
        const litteral = texteLitteralDe(indice);
        return litteral === null || enIndiceUnicode(litteral) === null;
    }
    return false;
}
/**
 * `true` si l'expression a besoin d'une MISE EN PAGE À DEUX DIMENSIONS —
 * c'est-à-dire si l'écrire sur une seule ligne de texte lui ferait perdre du
 * sens ou de la lisibilité.
 *
 * C'est la décision la plus importante du moteur, et elle est prise ICI, une
 * fois, pour les deux rendus. Elle sépare deux régimes :
 *
 *  - `20 − 12 ÷ 4`, `4a − 1`, `3²` : une ligne de texte suffit, et le texte
 *    a deux avantages que le SVG n'aura jamais — il se COUPE en fin de ligne
 *    et il grossit avec les réglages d'accessibilité du système. On ne lève
 *    donc pas la mise en page pour rien.
 *  - `2/3 + 1/4`, `√(x + 1)`, un système : là, et seulement là, on dessine.
 *
 * C'est aussi ce qui garantit qu'AUCUN écran ne régresse : tout ce qui
 * s'affiche correctement aujourd'hui reste du texte, exactement le même.
 */
export function exigeDeuxDimensions(noeud) {
    return contientMath(noeud, (n) => n.type === 'fraction' ||
        n.type === 'racine' ||
        n.type === 'valeurAbsolue' ||
        n.type === 'norme' ||
        n.type === 'surmonte' ||
        n.type === 'systeme' ||
        n.type === 'grandOperateur' ||
        n.type === 'sousAccolade' ||
        n.type === 'matrice' ||
        (n.type === 'script' && scriptExigeDessin(n)));
}
//# sourceMappingURL=expression.js.map